import makeStyles from "@mui/styles/makeStyles";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import React, {Fragment, memo, useEffect, useRef, useState} from "react";
import {Divider, FormLabel} from "@mui/material";
import AgyleTextField from "../../components/ui/fields/agyleTextField";
import AgyleDictionaryField from "../../components/ui/selector/agyleDictionaryField";
import config from "../../app/config";
import _ from "lodash";
import moment from "moment/moment";
import AgyleBlockSwitch from "../../components/ui/switch/agyleBlockSwitch";
import AgyleButton from "../../components/ui/buttons/agyleButton";
import AddIcon from "@mui/icons-material/Add";
import AgyleDatePicker from "../../components/ui/fields/agyleDatePicker";
import useDict from "../../hooks/useDict";
import {useDispatch, useSelector} from "react-redux";
import {hasOneOfPermission, Permissions, user} from '../../store/reducers/user';
import Formatter from "../../util/formatter";
import useOpenState from "../../hooks/useOpenState";
import {useTranslation} from "react-i18next";
import MessageBox from "../../util/messageBox";
import ReferralsForm from "../ward/forms/referralsForm";
import MainScreenButton from "../../components/forms/mainScreenButton";
import DoneIcon from "@mui/icons-material/Done";
import PatientAlarms from "../ward/forms/alarms/patientAlarms";
import clsx from "clsx";
import NotificationsIcon from "@mui/icons-material/Notifications";
import Typography from "@mui/material/Typography";
import API from "../../app/api";
import useCliniciansAndNursesDict from '../../hooks/useCliniciansAndNursesDict';
import {calculateArrivalDate} from '../../util/patientUtil';
import {getFacility, getInfectionIcon} from '../ward/forms/formsUtil';

const useStyles = makeStyles((theme) => ({
  content: {
    padding: 20,
    margin: 20,
  },
  background: {
    backgroundColor: theme.palette.background.default,
  },
  dialogTitle: {
    textAlign: "center",
    padding: "10px",
  },
  dialogContent: {
    width: "100%",
  },
  groupRoot: {
    marginBottom: 10,
    padding: 5,
    borderRadius: 5,
  },
  paper: {
    padding: 10,
    backgroundColor: theme.palette.background.default,
  },
  centerRow: {
    display: "flex",
    flexWrap: "nowrap",
    flexDirection: "row",
    alignItems: "center",
  },
  userLog: {
    fontSize: 11,
    fontStyle: "italic",
    color: theme.palette.mode === "light" ? "#4B4B4B" : "#818181",
  },
  comment: {
    fontSize: 15,
    padding: 5,
    backgroundColor: theme.palette.background.default,
    borderRadius: 5,
  },
  clinician: {
    marginBottom: 10,
  },
  row: {
    width: "100%",
    marginBottom: 10,
  },
  divider: {
    margin: "10px 0",
  }
}));

const BedBookingAdmit = (props) => {
  const classes = useStyles();
  const {t} = useTranslation();
  const dispatch = useDispatch();

  // User object
  const userObject = useSelector(user);
  const userFullName = Formatter.formatUserFullName(userObject);

  // Form state
  const [isReferralsOpen, openReferrals, closeReferrals] = useOpenState(false, true);

  // Comment
  const [isCommentOpen, openComment, closeComment] = useOpenState(false, true);
  const [comment, setComment] = useState("");
  const type = useRef(null);

  // Dict
  // const [cliniciansDict, cliniciansLoading] = useDict("Clinicians", null, "deactivated ne true");
  const [nursesCliniciansList] = useCliniciansAndNursesDict('', !config?.dropdownsWithCliniciansAndNurses);
  const [triageDepsDict, triageDepsLoading] = useDict("TriageDependencies");
  const [activityTreatment, activityTreatmentLoading] = useDict("ActivityTreatment", null);
  const [sdecSitesDict, sdecSitesDictLoading] = useDict("SdecSites");
  const [currentSiteBedBooking, currentSiteBedBookingLoading] = useDict("CurrentSitesBedBooking");
  const [wards, setWards] = useState([]);

  const canViewReferrals = dispatch(hasOneOfPermission([Permissions.AGYLE_CAN_VIEW_REFERRALS, Permissions.AGYLE_CAN_MANAGE_REFERRALS]));

  const {form, setForm, hospital} = props;

  const changeInitialData = (field, label, clinician, val = true, nullStateSwitch = false) => {
    let clone = _.cloneDeep(form);
    clone[field] = val;
    addUserChange(clone, field);

    if (clinician) {
      if (_.isNull(val) || (!nullStateSwitch && val === false)) {
        clone[`${field}Who`] = null;
        clone[`${field}When`] = null;
      } else {
        if (!clone[`${field}Who`]) {
          const clinicianUser = _.find(nursesCliniciansList, {userName: userObject?.userName});
          clone[`${field}Who`] = clinicianUser ? clinicianUser : null;
        }
        if (!clone[`${field}When`]) clone[`${field}When`] = moment().toISOString();
      }
    }

    setForm(clone);
  }

  useEffect(() => {
    const controller = new AbortController();
    API.getWards(controller.signal)
        .then(response => {
          if (response?.data?.__data) {
            setWards(_.orderBy(response?.data?.__data, ["wardName"], ["asc"]));
          }
        });

    setForm(prevState => ({
      ...prevState,
      anotherDestination: getFacility(hospital?.hospitalId),
    }));

    return () => {
      controller.abort();
    };
  }, []);

  useEffect(() => {
    if (!form.requested) {
      changeInitialData("requested", "Requested", true)
    }
    if (form.patientPost === null) {
      setForm(prevState => ({
        ...prevState,
        patientPost: false
      }))
    }
  }, [form])

  useEffect(() => {
    if (props.hospital?.hospitalId && currentSiteBedBooking && !props.bedBookingForm?.currentSiteBedBooking) {
      const currentPlace = _.find(currentSiteBedBooking, {__code : props.hospital?.hospitalId});
      props.modifyValues([{
        name: "currentSiteBedBooking",
        value: currentPlace || null,
      }]);
    }
  }, [props.hospital, currentSiteBedBooking, props.bedBookingForm]);

  const addUserChange = (obj, field) => {
    obj[`${field}User`] = userFullName;
    obj[`${field}UserLogin`] = userObject?.userName;
    obj[`${field}Timestamp`] = moment().toISOString();
  };

  const handleAddComment = () => {
    let clone = _.cloneDeep(form);
    clone[`${type.current}Note`] = comment;
    addUserChange(clone, type.current);
    setForm(clone);

    setComment("");
    type.current = null;
    closeComment();
  };

  const handleCloseComment = (e, reason) => {
    if (reason !== "backdropClick") {
      setComment("");
      type.current = null;
      closeComment(e, reason);
    }
  };

  const handleChangeText = (e) => {
    const {value, name} = e.target;
    setForm(prevState => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleChangeDict = (name, value) => {
    setForm(prevState => ({
      ...prevState,
      [name]: value,
    }));
  };

  const renderComment = () => {
    const handleCommentChange = (e) => {
      const {value} = e.target;
      setComment(value);
    };

    return (
        <>
          <div>
            <FormLabel>
              {t("Text")}
            </FormLabel>
          </div>

          <AgyleTextField
              debounce
              name="comment"
              value={comment}
              autoFocus
              onChange={handleCommentChange}
          />
        </>
    );
  };

  const renderGroup = (field, label, clinician, commentLabel, nullStateSwitch, withinHour, noAsNull) => {
    const handleChangeSwitch = (val) => {
      let clone = _.cloneDeep(form);
      clone[field] = val;
      addUserChange(clone, field);

      if (clinician) {
        if (_.isNull(val) || (!nullStateSwitch && val === false)) {
          clone[`${field}Who`] = null;
          clone[`${field}When`] = null;
        } else {
          if (!clone[`${field}Who`]) {
            const clinicianUser = _.find(nursesCliniciansList, {userName: userObject?.userName});
            clone[`${field}Who`] = clinicianUser ? clinicianUser : null;
          }
          if (!clone[`${field}When`]) clone[`${field}When`] = moment().toISOString();
        }
      }

      // Add time to track
      if (withinHour) {
        clone.withinHourTimesToTrack = val === true ? moment().add(1, "hour").toISOString() : null;
      }

      setForm(clone);
    };

    const handleOpenComment = () => {
      type.current = field;
      setComment(form[`${field}Note`]);
      openComment();
    };

    const handleChangeClinician = (_e, val) => {
      let clone = _.cloneDeep(form);
      clone[`${field}Who`] = val;
      addUserChange(clone, field);
      setForm(clone);
    };

    const handleChangeDate = (val) => {
      let clone = _.cloneDeep(form);
      clone[`${field}When`] = moment(val).toISOString();
      addUserChange(clone, field);
      setForm(clone);
    };

    return (
        <>
          <div className={classes.groupRoot}>
            <Grid container direction="row" alignItems="center" justifyContent="space-between">
              <FormLabel>
                {t(label)}
              </FormLabel>
              <div className={classes.centerRow}>
                <AgyleBlockSwitch
                    onChange={handleChangeSwitch}
                    value={form[field]}
                    disabled={field === "requested"}
                    nullState={nullStateSwitch}
                    style={{marginRight: 10}}
                    customLabels={(!nullStateSwitch && !noAsNull) ? {no: t("N/R")} : null}
                />

                <div style={{margin: "0 5px"}}/>

                <AgyleButton
                    size="small"
                    onClick={handleOpenComment}
                    variant="contained"
                    startIcon={<AddIcon/>}
                    width="160px"
                >
                  {t(commentLabel || "Comment")}
                </AgyleButton>
              </div>
            </Grid>
            {!_.isEmpty(form[`${field}User`]) &&
                <div className={classes.userLog}>
                  {`${form[`${field}User`]} ${moment(form[`${field}Timestamp`]).format(config.defaultDateTimeFormat)}`}
                </div>
            }
            {!_.isEmpty(form[`${field}Note`]) &&
                <div className={classes.comment}>
                  {form[`${field}Note`]}
                </div>
            }
          </div>
          {clinician &&
              <Grid container direction="row" className={classes.clinician} spacing={2}>
                <Grid item xs={12} sm={6}>
                  <AgyleDictionaryField
                      label={`${label} Clinician`}
                      onChange={handleChangeClinician}
                      options={nursesCliniciansList}
                      groupBy={opt => config?.dropdownsWithCliniciansAndNurses ? opt.group : ""}
                      value={form[`${field}Who`]}
                      disabled={!form[field]}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <AgyleDatePicker
                      label={`${label} Date and Time`}
                      onChange={handleChangeDate}
                      value={form[`${field}When`]}
                      type="dateTime"
                      disabled={!form[field]}
                      maxDate={moment()}
                      minDate={calculateArrivalDate(props.patient)}
                  />
                </Grid>
              </Grid>
          }
        </>
    );
  };

  const renderAnotherDestination = () => {
    const handleChangeSwitch = (val) => {
      let clone = _.cloneDeep(form);
      clone.anotherDestination = val;
      addUserChange(clone, "anotherDestination");
      setForm(clone);
    };

    const handleOpenComment = () => {
      type.current = "anotherDestination";
      setComment(form[`anotherDestinationNote`]);
      openComment();
    };

    return (
        <>
          <div className={classes.groupRoot}>
            <Grid container direction="row" alignItems="center" justifyContent="space-between">
              <FormLabel>
                {t("Site bed is requested at")}
              </FormLabel>

              <div className={classes.centerRow}>
                <AgyleBlockSwitch
                    onChange={handleChangeSwitch}
                    value={form.anotherDestination}
                    style={{marginRight: 10}}
                    customLabels={{yes: t("Poole"), no: t("Bournemouth")}}
                    valueObject={{"true": "Poole", "false": "Bournemouth"}}
                />

                <div style={{margin: "0 5px"}}/>

                <AgyleButton
                    size="small"
                    onClick={handleOpenComment}
                    variant="contained"
                    startIcon={<AddIcon/>}
                    width="160px"
                >
                  {t("Comment")}
                </AgyleButton>
              </div>
            </Grid>

            {!_.isEmpty(form[`anotherDestinationUser`]) &&
                <div className={classes.userLog}>
                  {`${form[`anotherDestinationUser`]} ${moment(form[`anotherDestinationTimestamp`]).format(config.defaultDateTimeFormat)}`}
                </div>
            }

            {!_.isEmpty(form[`anotherDestinationNote`]) &&
                <div className={classes.comment}>
                  {form[`anotherDestinationNote`]}
                </div>
            }
          </div>
        </>
    );
  }

  const savedReferrals = () => {
    if (!_.isEmpty(props.bedBookingForm?.specialty?.ReferralsToOtherServices)) {
      return props.bedBookingForm?.specialty?.ReferralsToOtherServices?.length;
    }
    return 0;
  };

  const getLastAlarmUser = () => {
    const alarms = props.bedBookingForm?.alarms?.[0];

    if (_.isEmpty(alarms)) {
      return "-"
    }
    return _.last(alarms);
  }

  const getInfections = (pt, isBB) => {
    let obj = pt;

    if (isBB) {
      obj = {infections: {}};
      if (pt?.alarms && !_.isEmpty(pt?.alarms[1])) {
        _.forEach(pt?.alarms[1], al => {
          obj = {
            infections: {
              ...obj.infections,
              ...al,
            },
          };
        });
      }
    }

    let val = getInfectionIcon(obj, clsx(classes.topIcon, classes.centerRow), false, true, false);
    const openAlarms = () => {
      // openAlarm();
      props.openAlarmDialog();
    };

    if (!val && !isBB && obj?.alertsCount?.count && obj?.alertsCount?.count > 0) {
      val = (
          <div className={clsx(classes.topIcon, classes.centerRow)}>
            <NotificationsIcon/>
          </div>
      );
    }

    return {
      value: val,
      onClick: openAlarms,
    };
  };

  const renderReferralsText = (header) => {
    let completedReferrals = false;

    const checkFirstRef = () => {
      if (savedReferrals() > 0) {
        // Sort referrals by request date
        const sortRef = _.sortBy(props.bedBookingForm?.specialty?.ReferralsToOtherServices, (ref) => {
              return moment(`${ref.ActivityServiceRequestDate_EmergencyCare}T${ref.ActivityServiceRequestTime_EmergencyCare}`).toDate();
            },
        );
        // Filter only uncompleted referrals
        const filtered = _.filter(sortRef, (ref) => {
          return (_.isEmpty(ref.ReferredToServiceAssessmentDate) && _.isEmpty(ref.ReferredToServiceAssessmentTime));
        });

        if (!_.isEmpty(filtered)) {
          // Render first uncompleted refferal on button
          return `- ${filtered[0]?.ReferredToService_SnomedCt?.__description}`;
        } else {
          // Render last completed refferal on button
          completedReferrals = true;
          return `- ${
              _.last(sortRef)?.ReferredToService_SnomedCt?.__description
          }`;
        }
      }
      return 'Speciality Referrals';
    };

    const getHeader = () => {
      return savedReferrals() > 0 ? `[${savedReferrals()}]` : "";
    };

    if (header) {
      return (
          <Fragment>
            {t(`${getHeader()} ${checkFirstRef()}`)}
            {completedReferrals && (
                <span>
              <DoneIcon style={{position: "absolute"}}/>
            </span>
            )}
          </Fragment>
      );
    }

    return (
        <Fragment>
          {savedReferrals() > 0 && (
              <div className="label-set">
                {props?.bedBookingForm?.specialty &&
                    props?.bedBookingForm?.specialty && (
                        <Fragment>
                  <span className="label-part2">
                    {props?.bedBookingForm?.specialty?.by}
                  </span>
                          <span className="label-part3">
                    {" "}
                            {moment(props.bedBookingForm?.specialty?.date).format(
                                config.defaultDateTimeFormat,
                            )}
                  </span>
                        </Fragment>
                    )}
              </div>
          )}
        </Fragment>
    );
  };

  const handleChangeSdec = (_e, val) => {
      let valMap = val;
      if (val) {
        valMap = {
          ...valMap,
          _id: valMap.__id,
          description: valMap.__description,
        };
      }
    props.modifyValues([{
        name: "sdecSite",
        value: valMap,
      }]);
  }

  const handleChangeCurrentSite = (_e, val) => {
    props.modifyValues([{
        name: "currentSiteBedBooking",
        value: val,
      }]);
  }

  return (
    <Paper>
      <div className={classes.content}>
        <Grid container direction="row" className={classes.clinician} spacing={2}>
          <Grid item xs={12} sm={6}>
            <AgyleDictionaryField
                label={t("Patient Location")}
                onChange={handleChangeCurrentSite}
                options={currentSiteBedBooking}
                value={props.bedBookingForm?.currentSiteBedBooking}
                loading={currentSiteBedBookingLoading}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <AgyleDictionaryField
                label={t("Zone")}
                onChange={handleChangeSdec}
                options={_.filter(sdecSitesDict, f => f?.hospitalsId?.includes(props.bedBookingForm?.currentSiteBedBooking?.__code))}
                value={props.bedBookingForm?.sdecSite}
                loading={sdecSitesDictLoading}
            />
          </Grid>
        </Grid>

        {renderGroup("requested", "Requested", true, "ED Note")}

        <Divider className={classes.divider} />

        <Grid container direction="row" className={classes.clinician} spacing={2}>
          <Grid item xs={12} sm={6}>
            <AgyleDictionaryField
                label={t("ECDS Activity Treatment")}
                onChange={(_e, val) => handleChangeDict("activityTreatment", val)}
                options={activityTreatment}
                value={form.activityTreatment}
                loading={activityTreatmentLoading}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <AgyleDictionaryField
                label={t("Dependency")}
                onChange={(_e, value) => {
                  let clone = _.cloneDeep(props.bedBookingForm?.triage);
                  if (!clone) {
                    clone = {};
                  }
                  clone.dependency = value;
                  props.modifyValues([{name: "triage", value: clone}]);
                }}
                options={triageDepsDict}
                value={props.bedBookingForm?.triage?.dependency}
                loading={triageDepsLoading}
            />
          </Grid>
        </Grid>

        <div className={classes.row}>
          <AgyleTextField
              debounce
              label={t("Reason for Admissions")}
              name="reasonForAdmissions"
              value={form.reasonForAdmissions}
              onChange={handleChangeText}
              fullWidth
          />
        </div>

        <Divider className={classes.divider} />

        {renderGroup("sideRoom", "Side Room", false, "Reason")}
        {renderGroup("patientPost", "Patient Post Take Complete", false, "Comment", false, false, true)}

        <Divider className={classes.divider} />


        {config?.modules?.forms?.bedBooking?.enableAnotherDestination &&
            <>
              {renderAnotherDestination()}
              <Divider className={classes.divider} />
            </>
        }

        {renderGroup("allocated", "Allocated", true, "WARD Feedback")}

        <Divider className={classes.divider} />

        <Grid item xs={12}>
          <AgyleDictionaryField
              label={t("Ward")}
              onChange={(_e, val) => handleChangeDict("ward", val)}
              options={wards}
              value={form.ward}
              getOptionLabel={option => option.wardName}
          />
        </Grid>

        <Divider className={classes.divider} />

        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={6} sx={{ pl: 1 }}>
            <MainScreenButton
                label={renderReferralsText(true)}
                colorType={"notMandatory"}
                onOpen={openReferrals}
                filled={savedReferrals() > 0}
                disabled={!canViewReferrals}
                value={props.bedBookingForm?.specialty}
                spacialRender={renderReferralsText}
                admitButton
            />
          </Grid>

          <Grid item xs={12} sm={6} sx={{ pr: 1 }}>
            <MainScreenButton
                label={t("Patient Alarms")}
                colorType={"notMandatory"}
                onOpen={props.openAlarmDialog}
                filled={props.bedBookingForm?.alarms?.length > 0}
                value={getLastAlarmUser()}
                userPath={"userAdd"}
                datePath={"dateAdd"}
            />
            {props.bedBookingForm?.alarms && <Grid item xs={12} sm={6} sx={{pl: 1, mt: 2}}>
              <Typography variant="h6">
                {t("Infections")}
              </Typography>

              <div
                  onClick={e => {
                    // e.stopPropagation();
                    getInfections(props.bedBookingForm, true)?.onClick();
                  }
                  }
                  style={{display: 'inline-block', maxWidth: 'min-content'}}
              >
                {getInfections(props.bedBookingForm, true)?.value}
              </div>
            </Grid>}
          </Grid>

          <Divider className={classes.divider} />
        </Grid>
      </div>

      <ReferralsForm
          open={isReferralsOpen}
          patient={props.bedBookingForm}
          isBB
          fromAdmit
          onChange={(values) => props.modifyValues(values)}
          disableSave={props.disableSave}
          close={closeReferrals}
          modal
      />

      {props.isAlarmOpen &&
          <PatientAlarms
              patient={props.bedBookingForm}
              patientBB={props.bedBookingForm}
              open={props.isAlarmOpen}
              spellId={props.spellId}
              disableSave={props.disableSave}
              isBB
              fromAdmit
              onChange={(values) => props.modifyValues(values)}
              close={props.closeAlarmDialog}
          />
      }

      <MessageBox
          open={isCommentOpen}
          title={t("Add Comment")}
          haveAccept={{yes: t("Submit"), no: t("Close")}}
          onAccept={handleAddComment}
          onClose={handleCloseComment}
          messageObject={renderComment()}
      />
    </Paper>
  );
};

export default memo(BedBookingAdmit);
